import { useState } from 'react'
import { useServiceMutations } from '@/api/queries'
import type { ServiceInfo, ServiceInstance } from '@/api/types'
import { Badge, type Tone } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Icons } from '@/components/ui/Icons'
import { useToast } from '@/components/ui/Toast'
import styles from './MonitoringPage.module.css'
import { ScalabilityDialog } from './ScalabilityDialog'
import { ScaleDialog } from './ScaleDialog'
import { ServiceHistoryDialog } from './ServiceHistoryDialog'
import type { Target } from './target'

type DialogKind = 'scale' | 'configuration' | 'history'
type Confirm = { kind: 'reboot'; instance: ServiceInstance } | { kind: 'restartAll' }

function serviceStatus(service: ServiceInfo): { tone: Tone; label: string; title?: string } {
  if (service.status.toLowerCase() !== 'running') {
    return { tone: 'danger', label: service.status, title: service.unhealthyInstances > 0 ? `${service.unhealthyInstances} unhealthy` : undefined }
  }
  if (service.unhealthyInstances > 0) return { tone: 'warn', label: `${service.unhealthyInstances} unhealthy` }
  return { tone: 'ok', label: 'running' }
}

/** Instance states are free text from Azure, so the tone is a best guess on common wording. */
function instanceTone(status: string): Tone {
  const value = status.toLowerCase()
  if (/ready|running|started/.test(value)) return 'ok'
  if (/stop|fail|unhealth|unknown|error|deallocat/.test(value)) return 'danger'
  return 'warn'
}

function instanceCountText(service: ServiceInfo): string {
  // The API omits configuredInstances when it is null, so it arrives as undefined.
  const { configuredInstances, instanceCount } = service
  const unit = instanceCount === 1 && configuredInstances == null ? 'instance' : 'instances'
  return configuredInstances != null && configuredInstances !== instanceCount ? `${configuredInstances} / ${instanceCount} ${unit}` : `${instanceCount} ${unit}`
}

const plural = (count: number) => (count === 1 ? '' : 's')

interface ServiceRowProps {
  target: Target
  service: ServiceInfo
}

export function ServiceRow({ target, service }: ServiceRowProps) {
  const { reboot } = useServiceMutations()
  const toast = useToast()
  const [expanded, setExpanded] = useState(false)
  const [dialog, setDialog] = useState<DialogKind | null>(null)
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const [restarting, setRestarting] = useState(false)

  const isCloudService = service.type === 'CloudService'
  const status = serviceStatus(service)
  const mismatch = service.configuredInstances != null && service.configuredInstances !== service.instanceCount

  const rebootInstance = async (instance: ServiceInstance) => {
    try {
      await reboot.mutateAsync({ ...target, service: service.name, instance: instance.name })
      toast.notify(`Reboot requested for ${instance.name}`, 'success')
    } catch (err) {
      toast.error(err, `Reboot of ${instance.name} failed`)
    } finally {
      setConfirm(null)
    }
  }

  // One at a time, and it stops at the first failure so a bad call never takes the whole service down.
  const restartAll = async () => {
    const instances = service.instances
    let done = 0
    setRestarting(true)
    try {
      for (const instance of instances) {
        await reboot.mutateAsync({ ...target, service: service.name, instance: instance.name })
        done++
      }
      toast.notify(`Reboot requested for the ${done} instance${plural(done)} of ${service.name}`, 'success')
    } catch (err) {
      toast.error(err, `${service.name}: stopped after ${done} of ${instances.length} instances`)
    } finally {
      setRestarting(false)
      setConfirm(null)
    }
  }

  return (
    <li className={styles.service}>
      <div className={styles.serviceMain}>
        <IconButton
          label={`${expanded ? 'Hide' : 'Show'} the instances of ${service.name}`}
          aria-expanded={expanded}
          disabled={service.instances.length === 0}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <Icons.ChevronDown size={14} /> : <Icons.ChevronRight size={14} />}
        </IconButton>
        <span className={styles.serviceName} title={service.name}>
          {service.name}
        </span>
        <Badge tone={status.tone} title={status.title}>
          {status.label}
        </Badge>
      </div>

      <div className={styles.serviceMeta}>
        <Badge className={styles.typeBadge}>{service.type}</Badge>
        <span className={mismatch ? styles.mismatch : undefined} title={mismatch ? `${service.configuredInstances} configured, ${service.instanceCount} deployed` : undefined}>
          {instanceCountText(service)}
        </span>
        {service.size && <span title="Size">{service.size}</span>}
        {service.version && <span title="Version">{service.version}</span>}
      </div>

      <div className={styles.serviceActions}>
        {isCloudService && (
          <Button size="sm" onClick={() => setDialog('scale')}>
            Scale…
          </Button>
        )}
        <Button size="sm" onClick={() => setDialog('configuration')}>
          Configuration…
        </Button>
        <Button size="sm" onClick={() => setDialog('history')}>
          History…
        </Button>
        {isCloudService && (
          <Button size="sm" disabled={service.instances.length === 0} onClick={() => setConfirm({ kind: 'restartAll' })}>
            Restart all
          </Button>
        )}
      </div>

      {expanded && (
        <ul className={styles.instances}>
          {service.instances.map((instance) => (
            <li key={instance.name} className={styles.instance}>
              <span className={styles.instanceName} title={instance.name}>
                {instance.name}
              </span>
              <Badge tone={instanceTone(instance.status)}>{instance.status}</Badge>
              {instance.size && <span className={styles.muted}>{instance.size}</span>}
              {isCloudService && (
                <IconButton label={`Reboot ${instance.name}`} onClick={() => setConfirm({ kind: 'reboot', instance })}>
                  <Icons.Refresh size={13} />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
      )}

      {dialog === 'scale' && <ScaleDialog target={target} service={service} onClose={() => setDialog(null)} />}
      {dialog === 'configuration' && <ScalabilityDialog target={target} service={service} onClose={() => setDialog(null)} />}
      {dialog === 'history' && <ServiceHistoryDialog target={target} service={service} onClose={() => setDialog(null)} />}
      {confirm && (
        <ConfirmDialog
          title={confirm.kind === 'reboot' ? `Reboot ${confirm.instance.name}` : `Restart all instances of ${service.name}`}
          message={
            confirm.kind === 'reboot'
              ? `${confirm.instance.name} of ${service.name} (${target.environment}) is rebooted and is unavailable while it restarts.`
              : `The ${service.instances.length} instance${plural(service.instances.length)} of ${service.name} (${target.environment}) are rebooted one after the other. The sequence stops at the first failure.`
          }
          confirmLabel={confirm.kind === 'reboot' ? 'Reboot' : 'Restart all'}
          danger
          busy={reboot.isPending || restarting}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void (confirm.kind === 'reboot' ? rebootInstance(confirm.instance) : restartAll())}
        />
      )}
    </li>
  )
}
